import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toLocalDateInputValue(dateLike) {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function toLocalTimeInputValue(dateLike) {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function buildLocalISO(dateStr, timeStr) {
  const d = new Date(`${dateStr}T${timeStr}:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function RescheduleModal({ open, onClose, onSave, initialISO }) {
  const { t } = useTranslation();

  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const today = useMemo(() => toLocalDateInputValue(new Date()), []);
  const nowTs = Date.now();

  useEffect(() => {
    if (!open) return;

    setError("");
    setSaving(false);

    if (initialISO) {
      setDate(toLocalDateInputValue(initialISO));
      setTime(toLocalTimeInputValue(initialISO));
    } else {
      setDate("");
      setTime("");
    }
  }, [open, initialISO]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape" && !saving) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, saving, onClose]);

  if (!open) return null;

  const selectedISO = date && time ? buildLocalISO(date, time) : null;
  const selectedTs = selectedISO ? new Date(selectedISO).getTime() : null;
  const isPast = selectedTs !== null && selectedTs <= nowTs;

  const canSave = !!date && !!time && !isPast && !saving;

  const handleSave = async () => {
    setError("");

    if (!date || !time) {
      setError(t("bookings.rescheduleErrors.fillAllFields"));
      return;
    }

    const iso = buildLocalISO(date, time);
    if (!iso) {
      setError(t("bookings.rescheduleErrors.invalidDateTime"));
      return;
    }

    const ts = new Date(iso).getTime();
    if (!Number.isFinite(ts) || ts <= Date.now()) {
      setError(t("bookings.rescheduleErrors.futureOnly"));
      return;
    }

    try {
      setSaving(true);
      await onSave(iso);
    } catch {
      setError(t("bookings.rescheduleErrors.saveFailed"));
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      onClick={() => {
        if (!saving) onClose?.();
      }}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-[#272c39] p-5 text-white ring-1 ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold">
          {t("bookings.rescheduleLesson")}
        </h3>

        <p className="mt-1 text-sm text-white/60">
          {t("bookings.rescheduleHint")}
        </p>

        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-sm text-white/80">
              {t("profile.date")}
            </label>
            <input
              type="date"
              min={today}
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setError("");
              }}
              disabled={saving}
              className="w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7269e3] disabled:opacity-60"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm text-white/80">
              {t("profile.time")}
            </label>
            <input
              type="time"
              value={time}
              onChange={(e) => {
                setTime(e.target.value);
                setError("");
              }}
              disabled={saving}
              className="w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7269e3] disabled:opacity-60"
            />
          </div>
        </div>

        {isPast ? (
          <div className="mt-3 rounded-xl border border-red-300/20 bg-red-500/10 px-3 py-2 text-sm text-red-100">
            {t("bookings.rescheduleErrors.futureOnly")}
          </div>
        ) : null}

        {error ? (
          <div className="mt-3 rounded-xl border border-red-300/20 bg-red-500/10 px-3 py-2 text-sm text-red-100">
            {error}
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="rounded-xl border border-white/20 px-3 py-1.5 hover:bg-white/5 disabled:opacity-60"
          >
            {t("common.cancel")}
          </button>

          <button
            onClick={handleSave}
            disabled={!canSave}
            className="rounded-xl bg-[#7269e3] px-3 py-1.5 font-semibold hover:brightness-110 disabled:opacity-60"
          >
            {saving ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}